import { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { ApiInstance } from '@/lib/api'
import { fetchMyAccess } from '@/lib/access'
import { errorMessage } from '@/lib/errors'
import { clearPermissionsCache, setCompanyId } from '@/lib/session'
import { useAuth } from '@/providers/auth'
import type { UserInvite } from '@/pages/User/Invites/InviteCard'

export const PENDING_INVITES_KEY = ['pending-invites']
export const PENDING_INVITES_LIST_KEY = [...PENDING_INVITES_KEY, 'list']

export async function fetchPendingInvites(): Promise<UserInvite[]> {
  const { data } = await ApiInstance.get<{ rows?: UserInvite[] }>('/me/invites', { params: { query: 'status=pending' } })
  return data.rows ?? []
}

export function usePendingInvites() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { setSelectedCompany } = useAuth()
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const query = useQuery(PENDING_INVITES_LIST_KEY, fetchPendingInvites, { staleTime: 0 })

  const accept = async (invite: UserInvite) => {
    setActionLoading(`${invite.id}-accept`)
    try {
      await ApiInstance.post(`/me/invites/${invite.id}`, { accepted: true })
      const access = await queryClient.fetchQuery(['me', 'access'], fetchMyAccess, { staleTime: 0 }).catch(() => null)
      const name = access?.memberships.find(membership => membership.company.id === invite.companyId)?.company.name || invite.company?.name || 'Imobiliária'
      clearPermissionsCache()
      setCompanyId(invite.companyId)
      setSelectedCompany({ id: invite.companyId, name }, { silent: true })
      void queryClient.invalidateQueries(PENDING_INVITES_KEY)
      toast.success('Convite aceito', { description: `Você agora faz parte da equipe de ${name}.` })
      navigate('/', { replace: true })
    } catch (error) {
      toast.error(errorMessage(error, 'Não foi possível aceitar o convite'))
    } finally {
      setActionLoading(null)
    }
  }

  const reject = async (invite: UserInvite): Promise<boolean> => {
    setActionLoading(`${invite.id}-reject`)
    try {
      await ApiInstance.post(`/me/invites/${invite.id}`, { accepted: false })
      await queryClient.invalidateQueries(PENDING_INVITES_KEY)
      toast.success('Convite recusado')
      return true
    } catch (error) {
      toast.error(errorMessage(error, 'Não foi possível recusar o convite'))
      return false
    } finally {
      setActionLoading(null)
    }
  }

  return { query, invites: query.data ?? [], actionLoading, accept, reject }
}
